import { apiClient } from './client';

// ============================================
// Types
// ============================================

export type EntityType = 'contact' | 'packet' | 'checklist';

export interface LabelWithCount {
  name: string;
  objectCount: number;
}

export interface LabelCategory {
  name: string;
  labels: LabelWithCount[];
}

export interface LabelAction {
  name: string;
  action: 'add' | 'remove';
}

/**
 * Selection for label list/save calls.
 *
 * VERIFIED BACKEND CONTRACT (vingapi src/shared/labels): both endpoints take
 * EXPLICIT ids only.
 *   - GET  /api/labels/{type}/  honors `selected_ids` (CSV query param) for
 *     tri-state counts. `selected_all`/`excluded_ids`/filter params are
 *     silently ignored.
 *   - POST /api/labels/{type}/  takes selection in the BODY `ids` field.
 *     Query params are ignored entirely; an absent `ids` falls through to the
 *     backend's own filter resolver over the WHOLE context (capped at 500).
 *
 * Callers in select-all mode must materialize their selection to explicit ids
 * first (see src/lib/resolveBulkSelection.ts).
 */
export interface SelectionParams {
  selectedIds?: number[];
}

// ============================================
// API Functions
// ============================================

/**
 * Get labels for an entity type with counts relative to selection
 *
 * When selectedIds are provided, object_count reflects how many
 * of the selected items have each label (for tri-state calculation).
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param params - Selection parameters for tri-state calculation
 * @returns Labels grouped by category with counts
 */
export async function getLabels(
  entityType: EntityType,
  params: SelectionParams = {}
): Promise<LabelCategory[]> {
  const queryParams: Record<string, string> = {};
  if (params.selectedIds?.length) {
    queryParams.selected_ids = params.selectedIds.join(',');
  }

  const response = await apiClient.get<
    | {
        count: number;
        next: string | null;
        previous: string | null;
        results: Record<string, LabelWithCount[]>;
      }
    // Some endpoints return the array-of-categories shape directly. Accept
    // both so the caller doesn't have to special-case it.
    | Array<{ name: string; labels: Array<{ name: string; objectCount?: number; count?: number }> }>
  >(`/api/labels/${entityType}/`, { params: queryParams });

  // Shape A: array-of-categories
  if (Array.isArray(response.data)) {
    return response.data.map((cat) => ({
      name: cat.name,
      labels: cat.labels.map((label) => ({
        name: label.name,
        // Normalize legacy `count` field to canonical `objectCount`.
        objectCount: label.objectCount ?? label.count ?? 0,
      })),
    }));
  }

  // Shape B: { results: { category: LabelWithCount[] } }
  const labelsData = response.data.results || {};
  return Object.entries(labelsData).map(([categoryName, labels]) => ({
    name: categoryName,
    labels: labels.map((label) => ({
      name: label.name,
      objectCount: label.objectCount,
    })),
  }));
}

/**
 * Get label suggestions based on search query
 *
 * Uses the same endpoint as getLabels but with suggest=true parameter.
 * Returns labels matching the query text for the given entity type.
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param query - Search text to filter labels
 * @returns Array of suggested labels
 */
export async function getLabelSuggestions(
  entityType: EntityType,
  query: string
): Promise<LabelWithCount[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const response = await apiClient.get<{
    count: number;
    next: string | null;
    previous: string | null;
    results: Record<string, LabelWithCount[]>;
  }>(`/api/labels/${entityType}/`, {
    params: {
      suggest: true,
      query: query.trim(),
    },
  });

  const labelsData = response.data.results || {};

  // Flatten all categories into a single array of suggestions, then
  // deduplicate by normalized name so a label in two categories appears
  // once. Keep the first occurrence's display name; objectCount is the
  // max across duplicates (the server counts the same objects under each
  // category — summing would double-count).
  const flat = Object.values(labelsData).flat();
  const seen = new Map<string, LabelWithCount>();
  for (const entry of flat) {
    const key = normalizeLabelName(entry.name);
    const existing = seen.get(key);
    if (existing) {
      existing.objectCount = Math.max(existing.objectCount, entry.objectCount);
    } else {
      seen.set(key, { ...entry });
    }
  }
  return Array.from(seen.values());
}

/**
 * Save label changes (add/remove) for selected items.
 *
 * Selection travels in the BODY `ids` field — that is the only selection
 * input the backend reads (see {@link SelectionParams}). `ids` is required:
 * posting without it would let the backend's filter resolver apply the labels
 * to the whole context, which is never what a dialog-scoped save means.
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param labels - Array of label actions (add or remove)
 * @param ids - Explicit entity ids to apply the actions to
 */
export interface SaveLabelsResult {
  affected: number;
  /**
   * Network context only: how many selected contacts were owned by a member
   * org (read-only) and therefore skipped. Absent/0 for org context and for
   * non-contact entities. Lets the success toast report what actually changed.
   */
  skippedReadOnly?: number;
}

export async function saveLabels(
  entityType: EntityType,
  labels: LabelAction[],
  ids: number[]
): Promise<SaveLabelsResult> {
  if (ids.length === 0) {
    throw new Error(
      'saveLabels: explicit entity ids are required — an empty selection ' +
        'would label the entire context on the backend.',
    );
  }

  // The wire says `skipped_read_only`; apiClient's response interceptor has
  // camelCased the body by the time it lands here (#963).
  const res = await apiClient.post<Partial<SaveLabelsResult> | null>(
    `/api/labels/${entityType}/`,
    { labels, ids },
  );
  const data = res.data ?? {};
  return {
    affected: data.affected ?? ids.length,
    skippedReadOnly: data.skippedReadOnly ?? 0,
  };
}

/**
 * Delete a label from all items of a given entity type
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param labelName - The label name to delete
 */
export async function deleteLabel(
  entityType: EntityType,
  labelName: string
): Promise<void> {
  await apiClient.delete(
    `/api/labels/${entityType}/${encodeURIComponent(labelName)}/`
  );
}

/**
 * Rename a label across all items of a given entity type.
 * PUT /api/labels/{type}/{name}/  body: { name }
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param oldName - The label's current name (identifies the label)
 * @param newName - The new name
 */
export async function renameLabel(
  entityType: EntityType,
  oldName: string,
  newName: string
): Promise<void> {
  await apiClient.put(
    `/api/labels/${entityType}/${encodeURIComponent(oldName)}/`,
    { name: newName }
  );
}

// ============================================
// Validation Helpers
// ============================================

/**
 * Below this a typed name is just UNFINISHED, so the dialog stays quiet; at or
 * above it, an invalid name gets an inline explanation instead of silently
 * losing its Create affordance (#83). Exported for that single caller.
 */
export const LABEL_MIN_LENGTH = 2;
const LABEL_MAX_LENGTH = 40;
const LABEL_VALID_PATTERN = /^[a-zA-Z0-9\s-]+$/;

/**
 * Validate a label name
 *
 * @param name - Label name to validate
 * @returns true if valid, error message string if invalid
 */
export function validateLabelName(name: string): true | string {
  const trimmed = name.trim();

  if (trimmed.length < LABEL_MIN_LENGTH) {
    return `Labels must be at least ${LABEL_MIN_LENGTH} characters`;
  }

  if (trimmed.length > LABEL_MAX_LENGTH) {
    return `Labels must be ${LABEL_MAX_LENGTH} characters or less`;
  }

  if (!LABEL_VALID_PATTERN.test(trimmed)) {
    return 'Labels can only contain letters, numbers, spaces, and hyphens';
  }

  return true;
}

/**
 * Check if a label name is valid (for inline validation)
 */
export function isValidLabelName(name: string): boolean {
  return validateLabelName(name) === true;
}

/**
 * Normalize a label name: trim, case-fold, collapse internal whitespace runs
 * to one space.  Used by `labelsMatch` and for deduplication.
 */
export function normalizeLabelName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Collapse a label name for display / API send: trim and collapse internal
 * whitespace runs to one space, but preserve the original case.
 */
export function collapseLabelName(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

/**
 * Check if two label names are equivalent (case-insensitive)
 */
export function labelsMatch(name1: string, name2: string): boolean {
  return normalizeLabelName(name1) === normalizeLabelName(name2);
}

// ============================================
// Category Management API Functions
// ============================================

/**
 * Create a new label category (folder)
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param categoryName - Name for the new category
 * @param labels - Optional array of label names to add to the category
 */
export async function createLabelCategory(
  entityType: EntityType,
  categoryName: string,
  labels: string[] = []
): Promise<void> {
  await apiClient.post(
    `/api/labels/categories/${entityType}/${encodeURIComponent(categoryName)}/`,
    { labels }
  );
}

/**
 * Rename a label category
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param oldName - Current category name
 * @param newName - New category name
 */
export async function renameLabelCategory(
  entityType: EntityType,
  oldName: string,
  newName: string
): Promise<void> {
  await apiClient.post(
    `/api/labels/categories/${entityType}/${encodeURIComponent(oldName)}/`,
    { new_name: newName }
  );
}

/**
 * Delete a label category
 *
 * Labels in the category will become uncategorized.
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param categoryName - Name of the category to delete
 */
export async function deleteLabelCategory(
  entityType: EntityType,
  categoryName: string
): Promise<void> {
  await apiClient.delete(
    `/api/labels/categories/${entityType}/${encodeURIComponent(categoryName)}/`
  );
}

/**
 * Move labels to a category
 *
 * Labels will be removed from any other categories and placed in this one.
 * If the category doesn't exist, it will be created.
 *
 * @param entityType - 'contact' | 'packet' | 'checklist'
 * @param categoryName - Target category name
 * @param labels - Array of label names to move
 */
export async function moveLabelsToCategory(
  entityType: EntityType,
  categoryName: string,
  labels: string[]
): Promise<void> {
  await apiClient.post(
    `/api/labels/categories/${entityType}/${encodeURIComponent(categoryName)}/`,
    { labels }
  );
}
